import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {graphql} from 'react-relay';
import styled from 'styled-components';
import {ForecastQueryRenderer} from '../../../ForecastQueryRenderer';
import MissionControlHeader from '../type-script-files/MissionControlHeader';
import Util from '../../../forecast-app/shared/util/util';
import {useForecastFetchQuery} from '../../../forecast-app/shared/hooks/useForecastFetchQuery';
import UpdateNovaAgentMutation from '../../../mutations/nova/UpdateNovaAgentMutation';
import {UpdateNovaAgentInput} from '../../../mutations/nova/__generated__/UpdateNovaAgentMutation.graphql';
import RestoreNovaAgentVersionMutation from '../../../mutations/nova/RestoreNovaAgentVersionMutation';
import AttachNovaAgentFileMutation from '../../../mutations/nova/AttachNovaAgentFileMutation';
import RemoveNovaAgentFileMutation from '../../../mutations/nova/RemoveNovaAgentFileMutation';
import {MutationInput} from '../../../mutations/MutationTypes';
import {NovaStudioShell} from './shell/NovaStudioShell';
import {NovaReferenceFile} from './shell/NovaReferenceFiles';
import {canonicalMimeType} from './shared/novaFileTypes';
import {StudioAgentEntry, StudioCategoryEntry} from './shell/useNovaStudioShell';
import {NovaAgentVersionEntry} from './modals/NovaVersionHistoryModal';
import {NovaAgentShape} from './shared/novaAgentForm';
import {toRelayGlobalId} from './shared/relayGlobalId';
import {NovaAgentSavePayload, useNovaAgentDraft} from './state/useNovaAgentDraft';
import {NovaStudioPageDetailQuery$data} from './__generated__/NovaStudioPageDetailQuery.graphql';
import {
	NovaVariable,
	NOVA_FILE_READY_STATUS,
	buildIsTokenPermitted,
	fileToNovaVariable,
	isNovaPromptInput,
	isNovaVariableLifecycleState,
	isNovaVariableScope,
} from './shared/novaVariableLibrary';

// NovaAgentCategory/NovaAgent only expose the relay-encoded `id` (see
// graphql/src/graphql/type/NovaAgentCategoryType.js, NovaAgentType.js) — there is no separate
// scalar categoryId/agentId field on the category or on the agent's own id, so the raw domain
// id has to be decoded back out of it to key the shell's flat catalogue/agent lists.
export const novaStudioQuery = graphql`
	query NovaStudioPage_Query {
		viewer {
			actualPersonId
			...MissionControlHeader_viewer
			novaAgentCatalogue {
				id
				name
				isUncategorised
				agents(first: 1000) @connection(key: "NovaStudioCatalogue_agents") {
					edges {
						node {
							id
							name
							categoryId
							title
							description
							shape
						}
					}
				}
			}
			novaVariableLibrary {
				variables {
					token
					scope
					description
					lifecycleState
					permittedInputs
				}
				removedVariables {
					token
					scope
					description
					lifecycleState
					permittedInputs
				}
			}
		}
	}
`;

// Separate from `novaStudioQuery` above: the catalogue is metadata-only (deliberately excludes the
// prompt bodies, which can be large and are only ever needed one agent at a time). This is fetched
// on demand — see `NovaStudioPage`'s effect below — whenever the shell's selection changes, rather
// than folded into the catalogue query or fetched for every agent up front.
export const novaAgentDetailQuery = graphql`
	query NovaStudioPageDetailQuery($id: ID!) {
		viewer {
			novaAgent(id: $id) {
				id
				title
				description
				systemPrompt
				userPromptTemplate
				agentMd
				categoryId
				currentVersionNumber
				versions {
					versionNumber
					createdAt
					createdBy
					isCurrent
				}
				files {
					fileId
					filename
					variableToken
					processingStatus
					sizeBytes
					mimeType
				}
			}
		}
	}
`;

interface CatalogueAgentNode {
	readonly id: string;
	readonly name: string | null;
	readonly categoryId: string | null;
	readonly title: string | null;
	readonly description: string | null;
	readonly shape: NovaAgentShape | null;
}

interface CatalogueAgentEdge {
	readonly node: CatalogueAgentNode | null;
}

interface CatalogueCategory {
	readonly id: string;
	readonly name: string | null;
	readonly isUncategorised: boolean | null;
	readonly agents: {
		readonly edges: ReadonlyArray<CatalogueAgentEdge | null> | null;
	} | null;
}

// `useForecastFetchQuery` has no request cancellation: selecting agent A then quickly agent B
// fires two in-flight fetches, and A's response can resolve after B's (or after B's own fetch
// has landed) since there is only one `data` slot. Loading whichever response lands last would
// silently overwrite the currently-selected agent's draft with a different agent's content, which
// a later Save would then persist onto the wrong record — so a response is only ever loaded when
// it still matches the currently selected agent.
export function isDetailResponseForSelectedAgent(
	fetchedAgentGlobalId: string | null | undefined,
	selectedAgentId: string | null
): boolean {
	if (!fetchedAgentGlobalId || !selectedAgentId) {
		return false;
	}
	return fetchedAgentGlobalId === toRelayGlobalId('NovaAgent', selectedAgentId);
}

// Why the detail fetch is re-run for two entirely different reasons, and why only one of them
// should ever load prompt content into the draft:
//   - 'select': the author just opened a (possibly different) agent — the draft has nothing of
//     its own yet for it, so the fetched content becomes the draft's starting point.
//   - 'refresh': a save the draft itself just made succeeded, and this fetch only exists to bring
//     `currentVersionNumber`/`versions` forward. The draft is already the source of truth for
//     prompt content at that point — if it loaded here too, any edit made between the save
//     resolving and this response landing would be silently reverted to the now-stale content this
//     response carries, with no error. So a 'refresh' response is applied nowhere but wherever a
//     later feature reads `currentVersionNumber`/`versions` off it directly.
export type DetailFetchReason = 'select' | 'refresh';

export function shouldLoadPromptContent(reason: DetailFetchReason): boolean {
	return reason === 'select';
}

// Builds the payload `updateNovaAgent` needs. `name` comes from the catalogue record (set at
// create time, not edited from the Prompt tab); the category, prompt bodies and the inline-edited
// title/description come from the working draft. `shape` is also create-time only and cannot be
// edited here — the server preserves the agent's stored shape on update regardless of what is
// sent (see NovaAgentLogic.updateAgent), so the catalogue's value is passed only to satisfy the
// non-null mutation input. Returns `null` when the draft has no category — so the caller can
// reject the save instead of sending a request the server would reject anyway.
export function buildUpdateNovaAgentInput(
	agent: StudioAgentEntry,
	values: NovaAgentSavePayload
): MutationInput<UpdateNovaAgentInput> | null {
	if (!values.categoryId) {
		return null;
	}
	return {
		id: toRelayGlobalId('NovaAgent', agent.agentId),
		name: agent.name,
		categoryId: values.categoryId,
		title: values.title,
		description: values.description,
		shape: agent.shape,
		systemPrompt: values.systemPrompt,
		userPromptTemplate: values.userPromptTemplate,
		agentMd: values.agentMd,
	};
}

export function mapCatalogueToShellData(catalogue: ReadonlyArray<CatalogueCategory | null> | null): {
	categories: StudioCategoryEntry[];
	agents: StudioAgentEntry[];
} {
	const knownCategories = (catalogue ?? []).filter((category): category is CatalogueCategory => category != null);

	const categories: StudioCategoryEntry[] = knownCategories.map(category => ({
		categoryId: Util.getUUIdFromBase64String(category.id),
		name: category.name ?? '',
		isUncategorised: category.isUncategorised ?? false,
	}));

	const agents: StudioAgentEntry[] = knownCategories.flatMap(category =>
		(category.agents?.edges ?? [])
			.filter((edge): edge is {node: CatalogueAgentNode} => edge?.node != null)
			.map(edge => ({
				agentId: Util.getUUIdFromBase64String(edge.node.id),
				name: edge.node.name ?? '',
				categoryId: edge.node.categoryId ?? '',
				title: edge.node.title ?? '',
				description: edge.node.description ?? '',
				shape: edge.node.shape ?? 'SINGLE_OUTPUT',
			}))
	);

	return {categories, agents};
}

// Full-height column so the MissionControl header sits above a Studio shell that fills the rest of
// the viewport, with the shell's inner scroll areas — the catalogue list and the tab content — and
// never the page itself absorbing the overflow.
//
// The height is set here, in viewport units, because this route renders outside the logged-in app
// frame: the MissionControl routes mount their page component directly, with no `100vh` flex column
// above them, so neither `flex: 1` nor `height: 100%` resolves to anything definite here and the
// column would grow to its content instead (measured: a 31-agent catalogue rendering 1214px tall in
// a 1080px viewport, scrolling the whole document and un-pinning the New-agent action). This column
// is the topmost element of the page — the MissionControl header is inside it — so the viewport
// height is exactly the budget it has to divide. `min-height: 0` lets its flex children shrink below
// their content, and `overflow: hidden` makes an unbounded descendant a visible bug here rather than
// a silent document-level scrollbar.
const StudioPageColumn = styled.div`
	display: flex;
	flex-direction: column;
	height: 100vh;
	min-height: 0;
	overflow: hidden;
`;

interface NovaStudioPageRelayProps {
	viewer: {
		novaAgentCatalogue: ReadonlyArray<CatalogueCategory | null> | null;
		novaVariableLibrary: {
			variables: ReadonlyArray<NovaVariable | null> | null;
			removedVariables: ReadonlyArray<NovaVariable | null> | null;
		} | null;
	};
}

// The raw, fully-nullable shape the library query returns. Every field can be null (the library is
// proxied verbatim from an externally-owned registry), so this maps into the clean, non-null
// `NovaVariable[]` the rest of the UI relies on — dropping anything unusable rather than trusting it.
interface RawNovaVariable {
	readonly token: string | null;
	readonly scope: string | null;
	readonly description: string | null;
	readonly lifecycleState: string | null;
	readonly permittedInputs: ReadonlyArray<string | null> | null;
}

export interface NovaVariableLibraryData {
	offered: NovaVariable[];
	removed: NovaVariable[];
}

function mapVariables(raw: ReadonlyArray<RawNovaVariable | null> | null | undefined): NovaVariable[] {
	const mapped: NovaVariable[] = [];
	for (const variable of raw ?? []) {
		// A variable with no token or an unrecognised scope can't be inserted or grouped, so it is
		// skipped rather than rendered broken. Unrecognised permitted inputs are filtered out (an
		// empty list then denies the variable everywhere — the safe default), and an unknown
		// lifecycle state falls back to ACTIVE.
		if (!variable || !variable.token || !isNovaVariableScope(variable.scope)) {
			continue;
		}
		const lifecycleState = isNovaVariableLifecycleState(variable.lifecycleState) ? variable.lifecycleState : 'ACTIVE';
		// A variable the registry marks removed must never be offered for insertion, even if the same
		// payload also lists it in `variables` — otherwise the panel renders it as a normal, clickable
		// row and clicking it immediately reports it as a broken reference.
		if (lifecycleState === 'REMOVED') {
			continue;
		}
		mapped.push({
			token: variable.token,
			scope: variable.scope,
			description: variable.description ?? '',
			lifecycleState,
			permittedInputs: (variable.permittedInputs ?? []).filter(isNovaPromptInput),
		});
	}
	return mapped;
}

// Removed entries only need a usable token to do their one job — letting a lingering reference read
// as broken rather than unknown. Scope is only ever read to group *offered* rows in the panel, which
// a removed entry never enters, so requiring a recognised scope here (as `mapVariables` does for
// offered rows) would drop a variable the registry retired at the same time as its scope, losing
// exactly the distinction this list exists to draw. An unrecognised scope or lifecycle state is
// therefore defaulted rather than treated as disqualifying.
function mapRemovedVariables(raw: ReadonlyArray<RawNovaVariable | null> | null | undefined): NovaVariable[] {
	const mapped: NovaVariable[] = [];
	for (const variable of raw ?? []) {
		if (!variable || !variable.token) {
			continue;
		}
		mapped.push({
			token: variable.token,
			scope: isNovaVariableScope(variable.scope) ? variable.scope : 'TASK',
			description: variable.description ?? '',
			lifecycleState: isNovaVariableLifecycleState(variable.lifecycleState) ? variable.lifecycleState : 'REMOVED',
			permittedInputs: (variable.permittedInputs ?? []).filter(isNovaPromptInput),
		});
	}
	return mapped;
}

// Splits the library into what authors may insert and what only exists so a lingering reference to
// it can be named as broken rather than unknown. The two are disjoint by construction: `mapVariables`
// excludes anything the registry marks removed, and nothing resolves a removed variable as offered.
export function mapVariableLibrary(
	library:
		| {
				readonly variables: ReadonlyArray<RawNovaVariable | null> | null;
				readonly removedVariables?: ReadonlyArray<RawNovaVariable | null> | null;
		  }
		| null
		| undefined
): NovaVariableLibraryData {
	return {offered: mapVariables(library?.variables), removed: mapRemovedVariables(library?.removedVariables)};
}

export interface NovaStudioPageContentProps {
	categories: StudioCategoryEntry[];
	agents: StudioAgentEntry[];
	variables: NovaVariable[];
	removedVariables: NovaVariable[];
	onRefetchCatalogue?: () => void;
}

// Split out from `NovaStudioPage` because it needs its own hooks (the working draft, the
// on-demand detail fetch) — those must live in a real component's body, not in the
// `ForecastQueryRenderer` render-prop callback itself, since that callback isn't guaranteed to
// run on every render of the same fiber (e.g. while the catalogue query is loading).
export function NovaStudioPageContent({
	categories,
	agents,
	variables,
	removedVariables,
	onRefetchCatalogue,
}: NovaStudioPageContentProps): JSX.Element {
	const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null);
	// Bumped after a successful save so the detail-fetch effect below re-runs for the *same*
	// selected agent — the version list/current version a later task reads off it are only ever
	// as fresh as this fetch, and a save is exactly the event that moves them forward.
	const [detailRefreshToken, setDetailRefreshToken] = useState(0);
	// Which of the two reasons above triggered the most recent (or in-flight) detail fetch — read
	// by the load effect below to decide whether this response is allowed to touch the draft.
	const [detailFetchReason, setDetailFetchReason] = useState<DetailFetchReason>('select');
	// The agentId whose real content was last successfully loaded into the draft via a 'select'
	// response (see the load effect below). `useForecastFetchQuery` surfaces no error state of its
	// own, so without tracking this separately, a detail fetch that fails (or simply hasn't landed
	// yet) for the selected agent would leave the draft on its initial empty/stale values with no
	// indication anything is wrong — the save-state pill would still read "Saved", and a Save from
	// there would silently persist blank prompt bodies over the agent's real content. Comparing this
	// against `selectedAgentId` (see `isDetailLoadedForSelected` below) is what Save is gated on.
	const [loadedAgentId, setLoadedAgentId] = useState<string | null>(null);

	// Selection changes are the only path that should ever seed the draft from fetched content, so
	// this is the only place `detailFetchReason` is ever set to 'select' — everywhere else that
	// triggers a fetch (i.e. a save) sets it to 'refresh' instead (see `handleSave` below).
	const handleSelectedAgentIdChange = useCallback((agentId: string | null): void => {
		setDetailFetchReason('select');
		setSelectedAgentId(agentId);
	}, []);

	// The live selection, readable from a mutation callback that resolved long after it was created.
	// A callback closes over the selection as it was at call time, so it cannot tell on its own whether
	// the author has since moved to another agent — see requestDetailRefresh below.
	const selectedAgentIdRef = useRef(selectedAgentId);
	selectedAgentIdRef.current = selectedAgentId;

	// Ask the detail fetch to re-run for an agent, from a mutation callback that has just changed it.
	//
	// The selection guard is the point. Both reasons misfire if the author has moved on in the meantime:
	//   - 'refresh' responses are barred from touching the draft (see shouldLoadPromptContent), so
	//     setting it for a stale agent makes the *new* agent's in-flight 'select' response land under
	//     'refresh'. Its content is never loaded, `loadedAgentId` stays on the previous agent, and the
	//     editors show the previous agent's prompt bodies under the new agent's header with Save
	//     disabled. Re-selecting the same agent doesn't re-fire the fetch effect, so that only clears by
	//     switching to a third agent and back.
	//   - 'select' does the opposite and reloads the draft, discarding whatever the author has typed
	//     into the agent they switched to.
	// Saves and restores are round-trips of milliseconds and rarely raced; a file upload is seconds,
	// which turns a narrow window into a routine one.
	const requestDetailFetch = useCallback((agentIdAtCallTime: string | null, reason: DetailFetchReason): void => {
		if (selectedAgentIdRef.current !== agentIdAtCallTime) {
			return;
		}
		setDetailFetchReason(reason);
		setDetailRefreshToken(token => token + 1);
	}, []);

	const handleSave = useCallback(
		(values: NovaAgentSavePayload): Promise<void> =>
			new Promise((resolve, reject) => {
				const agentIdAtCallTime = selectedAgentId;
				const agent = agents.find(candidate => candidate.agentId === agentIdAtCallTime);
				if (!agent) {
					reject(new Error('save requires a selected agent'));
					return;
				}
				const input = buildUpdateNovaAgentInput(agent, values);
				if (!input) {
					reject(new Error('updateNovaAgent requires a categoryId'));
					return;
				}
				Util.CommitMutation(
					UpdateNovaAgentMutation,
					input,
					() => {
						// The agent's connection membership is keyed off its category on the server;
						// Relay merges the updated node by id but can't move it between connections on
						// its own, so refetch when the category actually changed (mirrors the edit
						// modal's own save path in NovaStudioShell.tsx).
						if (values.categoryId !== agent.categoryId) {
							onRefetchCatalogue?.();
						}
						requestDetailFetch(agentIdAtCallTime, 'refresh');
						resolve();
					},
					true,
					null,
					(transaction, onFailure) => {
						// The fallback returns true when it has scheduled a retry of this same
						// mutation instead of surfacing an error (a transient network failure). That
						// isn't a final failure, so don't reject: rejecting would flip the draft to
						// its Error save-state while the retry is still in flight, and a retry that
						// then succeeds would leave the pill reading "error" over an agent that was
						// in fact saved. The retry's own success/failure settles this promise.
						if (onFailure(transaction) === true) {
							return;
						}
						reject(new Error('updateNovaAgent mutation failed'));
					}
				);
			}),
		[agents, selectedAgentId, onRefetchCatalogue, requestDetailFetch]
	);

	const detailFetch = useForecastFetchQuery<NovaStudioPageDetailQuery$data, {id: string}>(novaAgentDetailQuery);
	// A detail response can arrive for a since-abandoned selection (useForecastFetchQuery has no
	// cancellation) — only read or merge it while it still matches the current selection. This is the
	// same staleness guard the draft-loading effect below uses.
	const detailAgent = detailFetch.data?.viewer?.novaAgent;
	const isDetailForSelectedAgent = detailAgent != null && isDetailResponseForSelectedAgent(detailAgent.id, selectedAgentId);

	// The selected agent's attached documents — a list for the Reference files section, and (for READY
	// files) citable {file:...} variables merged onto the library. The merge is what lets insertion,
	// permission and broken-reference checks all see file variables (see novaInputValidation).
	const referenceFiles = useMemo<NovaReferenceFile[]>(() => {
		if (!isDetailForSelectedAgent || !detailAgent) {
			return [];
		}
		return (detailAgent.files ?? []).map(file => ({
			fileId: file.fileId,
			filename: file.filename,
			variableToken: file.variableToken,
			processingStatus: file.processingStatus,
			sizeBytes: file.sizeBytes ?? null,
		}));
	}, [isDetailForSelectedAgent, detailAgent]);

	const mergedVariables = useMemo<NovaVariable[]>(
		() => [
			...variables,
			...referenceFiles.filter(file => file.processingStatus === NOVA_FILE_READY_STATUS).map(fileToNovaVariable),
		],
		[variables, referenceFiles]
	);

	const isTokenPermitted = useMemo(() => buildIsTokenPermitted(mergedVariables), [mergedVariables]);
	const draft = useNovaAgentDraft({onSave: handleSave, isTokenPermitted});

	useEffect(() => {
		if (!selectedAgentId) {
			return;
		}
		// No onSuccess override: the effect below (keyed on `detailFetch.data`) is what actually loads
		// a matching response into the draft. onError only exists so a failed fetch is a known failure
		// rather than silent success — `loadedAgentId` simply never advances to this agent, which is
		// exactly what keeps Save blocked via `isDetailLoadedForSelected` below.
		detailFetch.fetch({id: toRelayGlobalId('NovaAgent', selectedAgentId)}, undefined, () => undefined);
		// Deliberately keyed on the selected agent id and the refresh token alone: `detailFetch.fetch`
		// is a fresh closure every render (see useForecastFetchQuery), so including it here would
		// re-fetch on every render rather than once per selection (or once per explicit refresh —
		// see `detailRefreshToken` above).
	}, [selectedAgentId, detailRefreshToken]);

	useEffect(() => {
		const agent = detailFetch.data?.viewer?.novaAgent;
		if (!agent || !isDetailResponseForSelectedAgent(agent.id, selectedAgentId)) {
			// Either nothing came back, or this response belongs to an agent that isn't the current
			// selection anymore (a stale, out-of-order fetch — see isDetailResponseForSelectedAgent) —
			// either way, there is nothing to load into the current draft.
			return;
		}
		if (!shouldLoadPromptContent(detailFetchReason)) {
			// A save-triggered refresh, not a selection — see `shouldLoadPromptContent` for why this
			// response must never reach `draft.load`: the draft already holds whatever the author has
			// typed since the save (possibly further edits made after it resolved), and loading here
			// would silently revert those to this now-stale snapshot.
			return;
		}
		// The detail fetch is the source for all of the draft's fields: the prompt bodies plus the
		// title/description/category, which the graphql layer promotes from the agent's current
		// version (the catalogue only carries name + categoryId, so it can't seed title/description).
		draft.load(
			{
				systemPrompt: agent.systemPrompt ?? '',
				userPromptTemplate: agent.userPromptTemplate ?? '',
				agentMd: agent.agentMd ?? '',
			},
			agent.categoryId ?? null,
			agent.title ?? '',
			agent.description ?? ''
		);
		// The draft now genuinely holds this agent's real content — record it as loaded so Save can
		// unblock (see `isDetailLoadedForSelected` below).
		setLoadedAgentId(selectedAgentId);
		// Deliberately keyed on the fetch result, the current selection, and the fetch reason alone:
		// `draft.load` is recreated every render along with the rest of the draft, so including it
		// here would re-run on every render rather than only when a new fetch result (or a newer
		// selection/reason that invalidates a stale one) actually calls for it.
	}, [detailFetch.data, selectedAgentId, detailFetchReason]);

	// The essential correctness gate: Save must never fire before the selected agent's real content
	// has actually been loaded into the draft (see `loadedAgentId` above for why this can't just be
	// "the fetch resolved" — a 'refresh' response never touches the draft either, so only a matching
	// 'select' response advances this). Threaded down to NovaStudioShell as `canSave`, which combines
	// it with the existing dirty/in-flight checks in `isSaveDisabled`.
	const isDetailLoadedForSelected = selectedAgentId != null && loadedAgentId === selectedAgentId;

	const versions: NovaAgentVersionEntry[] = useMemo(() => {
		if (!isDetailForSelectedAgent || !detailAgent) {
			return [];
		}
		return detailAgent.versions.map(version => ({
			versionNumber: version.versionNumber,
			createdAt: version.createdAt ?? null,
			createdBy: version.createdBy ?? null,
			isCurrent: version.isCurrent,
		}));
	}, [isDetailForSelectedAgent, detailAgent]);

	// Restoring a version is, from the draft's point of view, exactly like opening a different
	// agent: the fetched content becomes the new starting point and any local edits are meant to
	// be discarded (that's the whole point of restoring) — so this reuses the 'select' detail-fetch
	// reason (see `shouldLoadPromptContent`) to load the restored content once the follow-up fetch
	// below lands, rather than introducing a third reason that would behave identically.
	const handleRestoreVersion = useCallback(
		(versionNumber: number): Promise<void> =>
			new Promise((resolve, reject) => {
				const agentIdAtCallTime = selectedAgentId;
				if (!agentIdAtCallTime) {
					reject(new Error('restore requires a selected agent'));
					return;
				}
				Util.CommitMutation(
					RestoreNovaAgentVersionMutation,
					{id: toRelayGlobalId('NovaAgent', agentIdAtCallTime), versionNumber},
					() => {
						requestDetailFetch(agentIdAtCallTime, 'select');
						resolve();
					},
					true,
					null,
					(transaction, onFailure) => {
						// Same retry-in-flight guard as the save path above: a scheduled retry is not
						// a final failure, so the version-history modal keeps its rows disabled until
						// the retry itself settles instead of re-enabling them mid-flight.
						if (onFailure(transaction) === true) {
							return;
						}
						reject(new Error('restoreNovaAgentVersion mutation failed'));
					}
				);
			}),
		[selectedAgentId, requestDetailFetch]
	);

	// Attaching or removing a reference document is agent-scoped and does not touch the draft, so both
	// use the 'refresh' detail-fetch reason: the re-run brings the new `files` (and the Files-scope
	// variables derived from them) forward without reloading — and so silently reverting — any prompt
	// edits the author has made. The raw File rides as a Relay uploadable (5th arg); the server streams
	// it to S3 and records the metadata.
	const handleAttachFile = useCallback(
		(file: File): Promise<void> =>
			new Promise((resolve, reject) => {
				const agentIdAtCallTime = selectedAgentId;
				if (!agentIdAtCallTime) {
					reject(new Error('attach requires a selected agent'));
					return;
				}
				Util.CommitMutation(
					AttachNovaAgentFileMutation,
					{
						id: toRelayGlobalId('NovaAgent', agentIdAtCallTime),
						filename: file.name,
						// Derive the MIME from the extension: the browser's File.type is empty/inconsistent
						// for several supported types (.md, .csv), and the server validates by MIME. The
						// component only ever attaches files that pass validateNovaFile, so this is non-null.
						mimeType: canonicalMimeType(file.name) ?? file.type,
						sizeBytes: file.size,
					},
					() => {
						requestDetailFetch(agentIdAtCallTime, 'refresh');
						resolve();
					},
					true,
					{file},
					(transaction, onFailure) => {
						if (onFailure(transaction) === true) {
							return;
						}
						reject(new Error('attachNovaAgentFile mutation failed'));
					}
				);
			}),
		[selectedAgentId, requestDetailFetch]
	);

	const handleRemoveFile = useCallback(
		(fileId: string): Promise<void> =>
			new Promise((resolve, reject) => {
				const agentIdAtCallTime = selectedAgentId;
				if (!agentIdAtCallTime) {
					reject(new Error('remove requires a selected agent'));
					return;
				}
				Util.CommitMutation(
					RemoveNovaAgentFileMutation,
					{id: toRelayGlobalId('NovaAgent', agentIdAtCallTime), fileId},
					() => {
						requestDetailFetch(agentIdAtCallTime, 'refresh');
						resolve();
					},
					true,
					null,
					(transaction, onFailure) => {
						if (onFailure(transaction) === true) {
							return;
						}
						reject(new Error('removeNovaAgentFile mutation failed'));
					}
				);
			}),
		[selectedAgentId, requestDetailFetch]
	);

	return (
		<NovaStudioShell
			categories={categories}
			agents={agents}
			draft={draft}
			variables={mergedVariables}
			removedVariables={removedVariables}
			versions={versions}
			onRestoreVersion={handleRestoreVersion}
			canSave={isDetailLoadedForSelected}
			onSelectedAgentIdChange={handleSelectedAgentIdChange}
			onRefetchCatalogue={onRefetchCatalogue}
			referenceFiles={referenceFiles}
			onAttachFile={handleAttachFile}
			onRemoveFile={handleRemoveFile}
		/>
	);
}

export default function NovaStudioPage(): JSX.Element {
	return (
		<ForecastQueryRenderer
			query={novaStudioQuery}
			variables={{}}
			render={(relayProps: NovaStudioPageRelayProps, retry: (() => void) | null) => {
				const {categories, agents} = mapCatalogueToShellData(relayProps.viewer.novaAgentCatalogue);
				const library = mapVariableLibrary(relayProps.viewer.novaVariableLibrary);
				// A new category, or moving an agent between categories, can't be patched into the
				// store, so the shell asks for a full refetch via the render-prop's retry.
				return (
					<StudioPageColumn>
						{/* The viewer carries `...MissionControlHeader_viewer` at runtime (see the query
						    above); the render-prop's viewer type is intentionally loose, so narrow it to
						    exactly the header's expected fragment ref here. */}
						<MissionControlHeader
							viewer={relayProps.viewer as unknown as React.ComponentProps<typeof MissionControlHeader>['viewer']}
						/>
						<NovaStudioPageContent
							categories={categories}
							agents={agents}
							variables={library.offered}
							removedVariables={library.removed}
							onRefetchCatalogue={retry ?? undefined}
						/>
					</StudioPageColumn>
				);
			}}
		/>
	);
}
